import { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  Link,
  useTheme,
  MenuItem,
  IconButton,
  Tooltip,
  Popover,
  Button,
} from "@mui/material";
import Grow from "@mui/material/Grow";
import {
  Launch as LaunchIcon,
  Psychology as PsychologyIcon,
  GitHub as GitHubIcon,
  SportsEsports as SportsEsportsIcon,
  Assistant as AssistantIcon,
  CloudOutlined as CloudOutlinedIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from "@mui/icons-material";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import CallIcon from "@mui/icons-material/Call";
import GroupIcon from "@mui/icons-material/Group";
import { useNavigate } from "react-router-dom";
import { keyframes } from "@mui/system";
import { alpha } from "@mui/material/styles";
import DialogImage from "./DialogImage";

// Define the gradient animation
const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const AssistantSelection = ({ assistants, setAssistants }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [hoveredIcons, setHoveredIcons] = useState({});
  const [hoveredCards, setHoveredCards] = useState({});
  const [hoveredCloudIcons, setHoveredCloudIcons] = useState({});
  const [filteredAssistants, setFilteredAssistants] = useState([]);
  const [isLocalhost, setIsLocalhost] = useState(false);
  
  // State for model and communication style selectors
  const [modelAnchorEl, setModelAnchorEl] = useState({});
  const [commStyleAnchorEl, setCommStyleAnchorEl] = useState({});

  // State for managing the DialogImage
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [currentDiagram, setCurrentDiagram] = useState(null);

  // Check if we're on localhost and filter assistants accordingly
  useEffect(() => {
    const hostname = window.location.hostname;
    const isLocal = hostname === "localhost" || hostname === "127.0.0.1";
    setIsLocalhost(isLocal);

    // Filter assistants based on their active status and whether we're on localhost
    const filtered = isLocal
      ? assistants // Show all assistants on localhost
      : assistants.filter((assistant) => assistant.active === true); // Only show active assistants on other domains

    setFilteredAssistants(filtered);
  }, [assistants]);

  const handleSelectAssistant = (assistantId) => {
    navigate(`/assistant/${assistantId}`);
  };

  const handleMouseEnterIcon = (assistantId) => {
    setHoveredIcons((prev) => ({ ...prev, [assistantId]: true }));
  };

  const handleMouseLeaveIcon = (assistantId) => {
    setHoveredIcons((prev) => ({ ...prev, [assistantId]: false }));
  };

  const handleMouseEnterCard = (assistantId) => {
    setHoveredCards((prev) => ({ ...prev, [assistantId]: true }));
  };

  const handleMouseLeaveCard = (assistantId) => {
    setHoveredCards((prev) => ({ ...prev, [assistantId]: false }));
  };

  const handleMouseEnterCloudIcon = (assistantId, event) => {
    event.stopPropagation();
    setHoveredCloudIcons((prev) => ({ ...prev, [assistantId]: true }));
  };

  const handleMouseLeaveCloudIcon = (assistantId, event) => {
    event.stopPropagation();
    setHoveredCloudIcons((prev) => ({ ...prev, [assistantId]: false }));
  };

  // Model selector handlers
  const handleOpenModelSelect = (assistantId, event) => {
    event.stopPropagation();
    setModelAnchorEl(prev => ({
      ...prev,
      [assistantId]: event.currentTarget
    }));
  };

  const handleCloseModelSelect = (assistantId, event) => {
    if (event) event.stopPropagation();
    setModelAnchorEl(prev => ({
      ...prev,
      [assistantId]: null
    }));
  };

  // Communication style selector handlers
  const handleOpenCommStyleSelect = (assistantId, event) => {
    event.stopPropagation();
    setCommStyleAnchorEl(prev => ({
      ...prev,
      [assistantId]: event.currentTarget
    }));
  };

  const handleCloseCommStyleSelect = (assistantId, event) => {
    if (event) event.stopPropagation();
    setCommStyleAnchorEl(prev => ({
      ...prev,
      [assistantId]: null
    }));
  };

  const handleModelChange = (assistantId, modelKey, event) => {
    event.stopPropagation();
    const updatedAssistants = assistants.map(assistant => {
      if (assistant.id === assistantId) {
        return {
          ...assistant,
          selected_model: parseInt(modelKey)
        };
      }
      return assistant;
    });
    setAssistants(updatedAssistants);
    handleCloseModelSelect(assistantId, event);
  };

  const handleCommunicationStyleChange = (assistantId, styleKey, event) => {
    event.stopPropagation();
    const updatedAssistants = assistants.map(assistant => {
      if (assistant.id === assistantId) {
        return {
          ...assistant,
          selected_communication_style: parseInt(styleKey)
        };
      }
      return assistant;
    });
    setAssistants(updatedAssistants);
    handleCloseCommStyleSelect(assistantId, event);
  };

  // Updated to open the DialogImage
  const handleViewDiagram = (assistantId, event) => {
    event.stopPropagation();
    const assistant = assistants.find((a) => a.id === assistantId);
    if (assistant && assistant.diagram) {
      setCurrentDiagram(assistant.diagram);
      setImageDialogOpen(true);
    }
  };

  // Handler to close the dialog image
  const handleCloseImageDialog = () => {
    setImageDialogOpen(false);
    setCurrentDiagram(null);
  };

  return (
    <Box>
      <Grow
        in={true}
        timeout={{ enter: 600, exit: 300 }}
        style={{ transformOrigin: "50% 50% 0" }}
        mountOnEnter
        unmountOnExit
      >
        <Container maxWidth="xl" sx={{ pt: 14, pb: 8 }}>
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h3"
              fontWeight="500"
              sx={{
                background: (theme) =>
                  theme.palette.mode === "light"
                    ? "linear-gradient(90deg, #b636b9, #7760b9, #207a94, #009400)"
                    : "linear-gradient(90deg, #b636b9, #7760b9, #207a94, #009400)",
                backgroundSize: "350% auto",
                color: "transparent",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                animation: `${gradientAnimation} 4s ease infinite`,
                fontWeight: 500,
              }}
            >
              Amazon Bedrock Agents
            </Typography>
            <Typography
              variant="p"
              sx={{ fontSize: "1.25rem", lineHeight: "2rem" }}
            >
              Enable generative AI applications to execute multi-step business
              tasks using natural language
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h5"
              fontWeight="500"
              sx={{
                color: (theme) => theme.palette.primary.dark,
              }}
              gutterBottom
            >
              Data Analyst Assistants
            </Typography>
            <Typography variant="body1">
              Powered by{" "}
              <Link
                href="https://aws.amazon.com/about-aws/whats-new/2024/11/inlineagents-agents-amazon-bedrock/"
                target="_blank"
                rel="noopener"
                sx={{
                  fontWeight: "bold",
                  color: "primary.dark",
                  textDecorationColor: "primary.dark",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                  "&:visited": {
                    textDecoration: "none",
                  },
                }}
              >
                InlineAgents
              </Link>
              , this feature enables interaction with specialized agents that
              access domain-specific tools, while providing centralized
              instruction management and agents customization. This approach
              empowers organizations to scale and deploy agents across various
              use cases.
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ mb: 6 }}>
            {filteredAssistants.map((assistant) => (
              <Grid
                item
                size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}
                key={assistant.id}
              >
                <Card
                  onMouseEnter={() => {
                    handleMouseEnterCard(assistant.id);
                    handleMouseEnterIcon(assistant.id);
                  }}
                  onMouseLeave={() => {
                    handleMouseLeaveCard(assistant.id);
                    handleMouseLeaveIcon(assistant.id);
                  }}
                  onClick={() => handleSelectAssistant(assistant.id)}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition:
                      "transform 0.2s, box-shadow 0.2s, background 0.3s ease",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(8px)",
                    position: "relative",
                    borderRadius: 4,
                    boxShadow: "rgba(0, 0, 0, 0.05) 0px 4px 12px",
                    background: hoveredCards[assistant.id]
                      ? `radial-gradient(circle at top left, ${alpha(
                          theme.palette.primary.main,
                          0.04
                        )}, rgba(255, 255, 255, 0.1) 40%)`
                      : "rgba(255, 255, 255, 0.1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px",
                      borderColor: "rgba(255, 255, 255, 0.5)",
                      cursor: "pointer",
                    },
                    "&:before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: 0,
                      borderRadius: "inherit",
                      padding: "1px",
                      background:
                        "linear-gradient(45deg, #f299ff, #b2a8ff, #75cfff, #62ff57)",
                      WebkitMask:
                        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                      pointerEvents: "none",
                      opacity: 0.5,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, pt: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 0,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                          gutterBottom
                          variant="h5"
                          component="h2"
                          fontWeight="500"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            transition: "color 0.3s ease",
                            color: hoveredCards[assistant.id]
                              ? theme.palette.primary.main
                              : "rgba(0, 0, 0, 0.75)",
                            mb: 0, // Remove bottom margin to fix alignment
                          }}
                        >
                          {assistant.id === "video-games-sales" ? (
                            <SportsEsportsIcon
                              sx={{
                                mr: 1,
                                transition: "color 0.3s ease",
                                color: hoveredCards[assistant.id]
                                  ? theme.palette.primary.main
                                  : "inherit",
                              }}
                            />
                          ) : assistant.id === "post-call-analysis" ? (
                            <CallIcon
                              sx={{
                                mr: 1,
                                transition: "color 0.3s ease",
                                color: hoveredCards[assistant.id]
                                  ? theme.palette.primary.main
                                  : "inherit",
                              }}
                            />
                          ) : (
                            <AssistantIcon
                              sx={{
                                mr: 1,
                                transition: "color 0.3s ease",
                                color: hoveredCards[assistant.id]
                                  ? theme.palette.primary.main
                                  : "inherit",
                              }}
                            />
                          )}
                          {assistant.title}
                          {isLocalhost && !assistant.active && (
                            <Chip
                              size="small"
                              label="Inactive"
                              color="warning"
                              sx={{ ml: 1, height: 20 }}
                            />
                          )}
                          <LaunchIcon
                            fontSize="small"
                            sx={{
                              ml: 1,
                              opacity: hoveredCards[assistant.id] ? 1 : 0.25,
                              transition: "opacity 0.2s, color 0.3s ease",
                              color: hoveredCards[assistant.id]
                                ? theme.palette.primary.main
                                : "inherit",
                            }}
                            className="MuiSvgIcon-root"
                          />
                        </Typography>

                        {assistant.diagram && (
                          <Tooltip
                            title={assistant.diagram.title}
                            arrow
                            placement="top"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: "white",
                                  color: "rgba(0, 0, 0, 0.87)",
                                  boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.15)",
                                  borderRadius: "4px",
                                  fontSize: "0.75rem",
                                  textAlign: "center",
                                },
                              },
                              arrow: {
                                sx: {
                                  color: "white",
                                },
                              },
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) =>
                                handleViewDiagram(assistant.id, e)
                              }
                              onMouseEnter={(e) =>
                                handleMouseEnterCloudIcon(assistant.id, e)
                              }
                              onMouseLeave={(e) =>
                                handleMouseLeaveCloudIcon(assistant.id, e)
                              }
                              sx={{
                                ml: 1,
                                opacity: hoveredCloudIcons[assistant.id]
                                  ? 1
                                  : hoveredCards[assistant.id]
                                  ? 0.8
                                  : 0.4,
                                transition:
                                  "opacity 0.2s, color 0.3s ease, transform 0.2s",
                                color: hoveredCloudIcons[assistant.id]
                                  ? theme.palette.primary.main
                                  : "inherit",
                                transform: hoveredCloudIcons[assistant.id]
                                  ? "scale(1.1)"
                                  : "scale(1)",
                                "&:hover": {
                                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                                },
                              }}
                            >
                              <CloudOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, mt: 1 }}
                    >
                      {assistant.description}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        mt: "auto",
                      }}
                    >
                      {/* Model Selection Button */}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => handleOpenModelSelect(assistant.id, e)}
                        startIcon={<PsychologyIcon />}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={{
                          borderRadius: 4,
                          textTransform: "none",
                          color: "text.secondary",
                          borderColor: "rgba(0, 0, 0, 0.12)",
                          backgroundColor: "rgba(255, 255, 255, 0.2)",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                            borderColor: theme.palette.primary.main,
                          },
                        }}
                      >
                        {assistant.models[assistant.selected_model].name}
                      </Button>
                      <Popover
                        open={Boolean(modelAnchorEl[assistant.id])}
                        anchorEl={modelAnchorEl[assistant.id]}
                        onClose={(e) => handleCloseModelSelect(assistant.id, e)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                        onClick={(e) => e.stopPropagation()}
                        PaperProps={{
                          sx: {
                            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                            borderRadius: 2,
                          }
                        }}
                      >
                        <Box sx={{ width: 200, p: 1 }}>
                          {Object.entries(assistant.models).map(([key, model]) => (
                            <MenuItem 
                              key={key} 
                              onClick={(e) => handleModelChange(assistant.id, key, e)}
                              selected={key === String(assistant.selected_model)}
                            >
                              {model.name}
                            </MenuItem>
                          ))}
                        </Box>
                      </Popover>
                      
                      {/* Communication Style Selection Button */}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => handleOpenCommStyleSelect(assistant.id, e)}
                        startIcon={<QuestionAnswerOutlinedIcon />}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={{
                          borderRadius: 4,
                          textTransform: "none",
                          color: "text.secondary",
                          borderColor: "rgba(0, 0, 0, 0.12)",
                          backgroundColor: "rgba(255, 255, 255, 0.2)",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                            borderColor: theme.palette.primary.main,
                          },
                        }}
                      >
                        {assistant.communication_styles[assistant.selected_communication_style].name}
                      </Button>
                      <Popover
                        open={Boolean(commStyleAnchorEl[assistant.id])}
                        anchorEl={commStyleAnchorEl[assistant.id]}
                        onClose={(e) => handleCloseCommStyleSelect(assistant.id, e)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                        onClick={(e) => e.stopPropagation()}
                        PaperProps={{
                          sx: {
                            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                            borderRadius: 2,
                          }
                        }}
                      >
                        <Box sx={{ width: 256, p: 1 }}>
                          {Object.entries(assistant.communication_styles).map(([key, style]) => (
                            <MenuItem 
                              key={key} 
                              onClick={(e) => handleCommunicationStyleChange(assistant.id, key, e)}
                              selected={key === String(assistant.selected_communication_style)}
                            >
                              {style.name}
                            </MenuItem>
                          ))}
                        </Box>
                      </Popover>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {filteredAssistants.length === 0 && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ width: "100%" }}>
                  No active assistants found.{" "}
                  {!isLocalhost && "Contact your administrator for access."}
                </Alert>
              </Grid>
            )}
          </Grid>

          {/* GitHub Repository Reference */}
          <Alert
            severity="info"
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              borderRadius: 4,
              boxShadow: "rgba(0, 0, 0, 0.04) 0px 4px 12px",
              background: "rgba(206, 211, 251, 0.1)",
              border: "1px solid rgba(140, 147, 251, 0.2)",
              "& .MuiAlert-icon": {
                color: "rgba(66, 165, 245, 0.9)",
              },
              "& .MuiAlert-message": {
                color: "rgba(13, 60, 97, 0.9)",
              },
            }}
            icon={<GitHubIcon sx={{ color: "#7C57E2" }} />}
          >
            <Typography variant="body2">
              Want to deploy your own Data Analyst Assistant? Explore our{" "}
              <Link
                href="https://github.com/awslabs/amazon-bedrock-agent-samples/tree/main/examples/agents_ux/video_games_sales_assistant_with_amazon_bedrock_agents"
                target="_blank"
                rel="noopener"
                sx={{
                  fontWeight: "bold",
                  color: "#0D3C61",
                  textDecorationColor: "#0D3C61",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                  "&:visited": {
                    textDecoration: "none",
                  },
                }}
              >
                GitHub repository
              </Link>{" "}
              for a ready-to-use reference implementation for Video Game Sales data.
            </Typography>
          </Alert>

          {/* Support Alert */}
          <Alert
            severity="success"
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              borderRadius: 4,
              boxShadow: "rgba(0, 0, 0, 0.04) 0px 4px 12px",
              background: alpha(theme.palette.secondary.light, 0.04),
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
              "& .MuiAlert-icon": {
                color: alpha(theme.palette.secondary.main, 0.9),
              },
              "& .MuiAlert-message": {
                color: alpha(theme.palette.secondary.dark, 0.9),
              },
            })}
            icon={<GroupIcon fontSize="inherit" sx={{ opacity: 0.75 }} />}
          >
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              Questions?, join our dedicated Slack channel:{" "}
              <strong>#multi-domain-data-analyst-assistant</strong>
            </Typography>
          </Alert>

          {isLocalhost && (
            <Alert
              severity="info"
              sx={{ mt: 2, width: "fit-content", opacity: 0.2 }}
            >
              Running in localhost mode: Showing all assistants (including
              inactive ones)
            </Alert>
          )}
        </Container>
      </Grow>

      {/* DialogImage */}
      <DialogImage
        open={imageDialogOpen}
        handleClose={handleCloseImageDialog}
        imagePath={currentDiagram?.image_path}
        title={currentDiagram?.title}
      />
    </Box>
  );
};

export default AssistantSelection;